import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const Logo = ({ light = false }) => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group shrink-0 min-w-0">
    <img
      src="https://customer-assets-cm19k8pv.emergentagent.net/job_distribution-hub-31/artifacts/mbaahi3y_FEE68BB0-B050-4EC7-BB36-BFFEEC714B51.png"
      alt="EEMA"
      className="h-10 md:h-11 w-auto object-contain shrink-0 rounded-sm transition-transform group-hover:scale-[1.03]"
      draggable="false"
    />
    <div className={`hidden md:block leading-tight border-l pl-3 ${light ? 'border-white/25' : 'border-slate-200'}`}>
      <div className={`text-[10px] tracking-[0.22em] uppercase font-display font-medium whitespace-nowrap ${light ? 'text-white/80' : 'text-[#0B1120]'}`}>European Electric</div>
      <div className={`text-[10px] tracking-[0.22em] uppercase font-display font-medium whitespace-nowrap ${light ? 'text-white/60' : 'text-[#4A5568]'}`}>Mobility Alliance</div>
    </div>
  </Link>
);

const LANGS = [
  { code: "en", label: "EN" },
  { code: "it", label: "IT" },
  { code: "de", label: "DE" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
  { code: "nl", label: "NL" },
  { code: "zh", label: "中文" },
];

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const changeLang = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("eema_lang", code);
    setLangOpen(false);
  };

  const links = [
    { to: "/about", label: t("nav.about") },
    { to: "/markets", label: t("nav.markets") },
    { to: "/services", label: t("nav.services") },
    { to: "/partners", label: t("nav.partners") },
    { to: "/for-manufacturers", label: t("nav.manufacturers") },
    { to: "/news", label: t("nav.news") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header data-testid="site-navbar" className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/60 shadow-sm' : 'bg-white/60 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between gap-6">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {links.map(l => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace(/\//g, '') || 'home'}`} className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <div className="relative">
            <button data-testid="lang-toggle" onClick={() => setLangOpen(o => !o)} className="flex items-center gap-1.5 text-sm font-display font-medium text-[#0B1120] hover:text-[#003399] transition-colors px-2 py-2">
              <Globe size={16} strokeWidth={1.5} />
              <span className="uppercase tracking-widest text-xs">{i18n.language}</span>
              <ChevronDown size={14} strokeWidth={1.5} />
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} className="absolute right-0 top-full mt-2 bg-white border border-slate-200 rounded-sm shadow-xl min-w-[80px] overflow-hidden">
                  {LANGS.map(l => (
                    <button key={l.code} data-testid={`lang-${l.code}`} onClick={() => changeLang(l.code)} className={`block w-full text-left px-4 py-2 text-sm font-display hover:bg-slate-50 ${i18n.language===l.code ? 'text-[#003399] font-semibold' : 'text-[#0B1120]'}`}>
                      {l.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/contact#partner" data-testid="nav-partner-cta" className="hidden md:inline-flex btn-primary">
            {t("nav.partner_cta")}
          </Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(o => !o)} className="lg:hidden text-[#0B1120] p-2">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="lg:hidden overflow-hidden bg-white border-t border-slate-200">
            <div className="px-6 py-6 flex flex-col gap-2">
              {links.map(l => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-nav-${l.to.replace(/\//g,'')||'home'}`} className={({isActive}) => `py-3 text-base font-display font-medium border-b border-slate-100 ${isActive ? 'text-[#003399]' : 'text-[#0B1120]'}`}>
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact#partner" data-testid="mobile-partner-cta" className="btn-primary mt-4">{t("nav.partner_cta")}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer data-testid="site-footer" className="bg-[#0B1120] text-white pt-24 pb-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          <div className="md:col-span-5">
            <Logo light />
            <p className="mt-6 text-white/70 leading-relaxed max-w-md font-body font-light">
              {t("footer.tagline")}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <a data-testid="footer-linkedin" href="https://linkedin.com" target="_blank" rel="noreferrer" className="h-10 w-10 rounded-sm border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#003399] transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.14 1.44-2.14 2.94v5.67H9.36V9h3.41v1.56h.05c.48-.9 1.63-1.85 3.36-1.85 3.59 0 4.26 2.36 4.26 5.44v6.3zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zm-1.78 13.02h3.55V9H3.56v11.45z"/></svg>
              </a>
              <a data-testid="footer-email" href="mailto:info@eema-group.com" className="text-sm text-white/70 hover:text-white transition-colors font-display">info@eema-group.com</a>
            </div>
          </div>
          <div className="md:col-span-3">
            <div className="text-xs tracking-[0.24em] uppercase text-white/50 mb-4 font-display">{t("footer.company")}</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.about")}</Link></li>
              <li><Link to="/markets" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.markets")}</Link></li>
              <li><Link to="/partners" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.partners")}</Link></li>
              <li><Link to="/for-manufacturers" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.manufacturers")}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.24em] uppercase text-white/50 mb-4 font-display">{t("footer.resources")}</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/services" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.services")}</Link></li>
              <li><Link to="/news" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.news")}</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("nav.contact")}</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs tracking-[0.24em] uppercase text-white/50 mb-4 font-display">{t("footer.legal")}</div>
            <ul className="space-y-3 text-sm">
              <li><a href="#" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("footer.privacy")}</a></li>
              <li><a href="#" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("footer.terms")}</a></li>
              <li><a href="#" className="text-white/80 hover:text-[#00E5FF] transition-colors">{t("footer.imprint")}</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} EEMA — European Electric Mobility Alliance. {t("footer.rights")}</div>
          <div className="font-display tracking-widest uppercase">Milan · Berlin · Zurich · Eindhoven · Vilnius</div>
        </div>
      </div>
    </footer>
  );
};
